/**
 * Screen picture for "Report a problem" (Bill 2026-10-07).
 *
 * Taken the moment the lifebuoy is clicked, of what the user was looking at -
 * the report dialog itself is left out. html2canvas-pro redraws the page from
 * the DOM (no screen-capture permission, works in the Teams desktop and iOS
 * webviews, bundled from npm - no CDN).
 *
 * Before drawing, the COPY of the page is scrubbed (the live page is never
 * touched): every text node and every field value goes through the same
 * tax-ID / card-number masking as the logs, fields whose name or label says
 * tax ID, SSN, account, card, PIN, password or birth date are blanked, and
 * anything marked data-sensitive is blanked. The user sees the result before
 * sending and can leave it out.
 */
import html2canvas from 'html2canvas-pro';
import { redactText } from './capture';

const EXCLUDE_SELECTOR = '.support-overlay, .support-fab, [data-support-exclude]';
const SENSITIVE_FIELD = /ssn|social|tax.?id|\btin\b|itin|atin|ein|account.?(no|num)|acct.?(no|num)|card|\bpan\b|\bpin\b|password|passcode|birth|\bdob\b/i;
const MAX_WIDTH = 1600;
const JPEG_QUALITY = 0.72;

export interface ScreenPicture { dataUrl: string; width: number; height: number }

function scrub(doc: Document): void {
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const t = n.nodeValue;
    if (t && /\d{9}|\d{3}-\d{2}-\d{4}|\d{12}/.test(t)) n.nodeValue = redactText(t);
  }
  doc.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea').forEach((el) => {
    const label = [el.name, el.id, el.getAttribute('autocomplete'), el.getAttribute('aria-label'), el.placeholder, (el as HTMLInputElement).type]
      .filter(Boolean).join(' ');
    if (el.value && SENSITIVE_FIELD.test(label)) {
      el.value = '•'.repeat(Math.min(el.value.length, 12));
    } else if (el.value) {
      el.value = redactText(el.value);
    }
    el.setAttribute('value', el.value);
  });
  doc.querySelectorAll<HTMLElement>('[data-sensitive]').forEach((el) => { el.textContent = '••••••'; });
}

/** Resolves null when the picture cannot be taken - a report never waits on or fails for it. */
export async function captureScreen(): Promise<ScreenPicture | null> {
  if (typeof document === 'undefined') return null;
  try {
    const canvas = await html2canvas(document.body, {
      logging: false,
      useCORS: true,
      backgroundColor: '#ffffff',
      x: window.scrollX,
      y: window.scrollY,
      width: window.innerWidth,
      height: window.innerHeight,
      scale: Math.min(window.devicePixelRatio || 1, 2),
      ignoreElements: (el) => el instanceof Element && el.matches(EXCLUDE_SELECTOR),
      onclone: (doc) => scrub(doc),
    });
    const ratio = canvas.width > MAX_WIDTH ? MAX_WIDTH / canvas.width : 1;
    const out = document.createElement('canvas');
    out.width = Math.round(canvas.width * ratio);
    out.height = Math.round(canvas.height * ratio);
    const ctx = out.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(canvas, 0, 0, out.width, out.height);
    return { dataUrl: out.toDataURL('image/jpeg', JPEG_QUALITY), width: out.width, height: out.height };
  } catch {
    return null;
  }
}
